import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, X, Mail, Sparkles, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import LandingNav from "@/components/LandingNav";
import SEOHead from "@/components/SEOHead";
import { useTranslation } from "react-i18next";

const Pricing = () => {
  const [userCount, setUserCount] = useState<number | "more">(1);
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const { t } = useTranslation();

  const userOptions = [1, 5, 10, 20];
  const pricePerUser = 10;
  const annualDiscount = 0.8; // 20% off

  const getPrice = (base: number) => {
    if (billing === "annual") return Math.round(base * annualDiscount);
    return base;
  };

  const plans = [
    {
      key: "free",
      name: t("pricing.plans.free.name"),
      price: "0€",
      period: "",
      description: t("pricing.plans.free.description"),
      features: [
        t("pricing.features.oneUserAccount"),
        t("pricing.features.upTo100Assets"),
        t("pricing.features.oneAssetList"),
        t("pricing.features.basicDataManagement"),
        t("pricing.features.allAppsExtensions"),
        t("pricing.features.mcpAccess"),
        t("pricing.features.limitedApiAccess")
      ]
    },
    {
      key: "starter",
      name: t("pricing.plans.starter.name"),
      price: `${getPrice(5)}€`,
      period: billing === "annual" ? t("pricing.perMonth") + " · " + t("pricing.billedAnnually") : t("pricing.perMonth"),
      description: t("pricing.plans.starter.description"),
      features: [
        t("pricing.features.oneUserAccount"),
        t("pricing.features.unlimitedAssets"),
        t("pricing.features.oneAssetList"),
        t("pricing.features.basicDataManagement"),
        t("pricing.features.allAppsExtensions"),
        t("pricing.features.mcpAccess"),
        t("pricing.features.limitedApiAccess")
      ],
      earlyAccess: true
    },
    {
      key: "professional",
      name: t("pricing.plans.professional.name"),
      price: userCount === "more" ? null : `${userCount * getPrice(pricePerUser)}€`,
      period: userCount === "more" ? null : (billing === "annual" ? t("pricing.perMonth") + " · " + t("pricing.billedAnnually") : t("pricing.perMonth")),
      description: t("pricing.plans.professional.description"),
      features: [
        userCount === "more" ? t("pricing.features.customUserCount") : t("pricing.usersCount", { count: userCount }),
        t("pricing.features.unlimitedAssets"),
        t("pricing.features.twentyAssetLists"),
        t("pricing.features.advancedDataManagement"),
        t("pricing.features.allAppsExtensions"),
        t("pricing.features.mcpAccess"),
        t("pricing.features.assetListSharing"),
        t("pricing.features.fullApiAccess")
      ],
      popular: true,
      hasUserSelect: true,
      earlyAccess: true
    },
    {
      key: "enterprise",
      name: t("pricing.plans.enterprise.name"),
      price: null,
      period: null,
      description: t("pricing.plans.enterprise.description"),
      features: [
        t("pricing.features.unlimitedUsers"),
        t("pricing.features.unlimitedAssets"),
        t("pricing.features.customAssetLists"),
        t("pricing.features.enterpriseDataManagement"),
        t("pricing.features.prioritySupport"),
        t("pricing.features.allAppsExtensions"),
        t("pricing.features.mcpAccess"),
        t("pricing.features.assetListSharing"),
        t("pricing.features.fullApiAccess")
      ],
      contactSales: true
    }
  ];

  // Comparison table rows
  const comparisonFeatures = [
    { label: t("pricing.comparison.users"), values: ["1", "1", t("pricing.comparison.unlimited"), t("pricing.comparison.unlimited")] },
    { label: t("pricing.comparison.assets"), values: ["100", t("pricing.comparison.unlimited"), t("pricing.comparison.unlimited"), t("pricing.comparison.unlimited")] },
    { label: t("pricing.comparison.assetLists"), values: ["1", "1", "20", t("pricing.comparison.custom")] },
    { label: t("pricing.comparison.sharing"), values: [false, false, true, true] },
    { label: t("pricing.comparison.apiAccess"), values: [t("pricing.comparison.limited"), t("pricing.comparison.limited"), t("pricing.comparison.full"), t("pricing.comparison.full")] },
    { label: t("pricing.comparison.appsExtensions"), values: [true, true, true, true] },
    { label: t("pricing.comparison.mcpAccess"), values: [true, true, true, true] },
    { label: t("pricing.comparison.prioritySupport"), values: [false, false, false, true] },
  ];

  const planNames = [
    t("pricing.plans.free.name"),
    t("pricing.plans.starter.name"),
    t("pricing.plans.professional.name"),
    t("pricing.plans.enterprise.name"),
  ];

  const faqItems = [
    { q: t("pricing.faq.switchPlans.question"), a: t("pricing.faq.switchPlans.answer") },
    { q: t("pricing.faq.afterPilot.question"), a: t("pricing.faq.afterPilot.answer") },
    { q: t("pricing.faq.contract.question"), a: t("pricing.faq.contract.answer") },
    { q: t("pricing.faq.dataOwnership.question"), a: t("pricing.faq.dataOwnership.answer") },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Pricing – TestAssets"
        description="Compare TestAssets plans. Free tier for individuals, Professional plans for QA teams. Test data management pricing."
        path="/pricing"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Pricing", url: "/pricing" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.a,
            },
          })),
        }}
      />
      <LandingNav />

      <main className="flex-1 py-16 px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Pilot Phase Banner */}
          <div className="mb-8 bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center justify-center gap-3">
            <Sparkles className="h-5 w-5 text-primary" />
            <p className="text-sm font-medium text-foreground">
              <span className="text-primary">{t("pricing.publicPilot")}</span> {t("pricing.pilotBanner")}
            </p>
          </div>

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t("pricing.title")}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              {t("pricing.description")}
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-muted">
              <button
                onClick={() => setBilling("monthly")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billing === "monthly"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t("pricing.monthly")}
              </button>
              <button
                onClick={() => setBilling("annual")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billing === "annual"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t("pricing.annual")}
                <span className="ml-1.5 text-xs text-primary font-semibold">{t("pricing.save20")}</span>
              </button>
            </div>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {plans.map((plan) => (
              <Card 
                key={plan.key} 
                className={`relative ${plan.popular ? 'border-primary shadow-lg' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                      {t("pricing.mostPopular")}
                    </span>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    {plan.price ? (
                      <>
                        <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                        <span className="text-muted-foreground ml-2">{plan.period}</span>
                      </>
                    ) : (
                      <span className="text-2xl font-semibold text-foreground">{t("pricing.customPricing")}</span>
                    )}
                  </div>
                  {plan.hasUserSelect && (
                    <div className="mt-4">
                      <label className="text-sm text-muted-foreground block mb-2">{t("pricing.numberOfUsers")}</label>
                      <Select
                        value={userCount.toString()}
                        onValueChange={(value) => setUserCount(value === "more" ? "more" : parseInt(value))}
                      >
                        <SelectTrigger className="w-full bg-background">
                          <SelectValue placeholder={t("pricing.numberOfUsers")} />
                        </SelectTrigger>
                        <SelectContent className="bg-popover z-50">
                          {userOptions.map((count) => (
                            <SelectItem key={count} value={count.toString()}>
                              {t("pricing.usersCount", { count })} - {count * getPrice(pricePerUser)}€/{t("pricing.perMonth").replace("per ", "")}
                            </SelectItem>
                          ))}
                          <SelectItem value="more">
                            {t("pricing.moreThan20Users")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {userCount === "more" && (
                        <div className="mt-3 p-3 bg-muted/50 rounded-lg border border-border">
                          <div className="flex items-center gap-2 text-primary">
                            <Mail className="h-5 w-5" />
                            <span className="font-medium">{t("pricing.contactSales")}</span>
                          </div>
                          <a 
                            href="mailto:support@testassets.io" 
                            className="text-sm text-muted-foreground hover:text-primary transition-colors mt-1 block"
                          >
                            support@testassets.io
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  {plan.contactSales ? (
                    <Button className="w-full" asChild>
                      <a href="mailto:support@testassets.io">{t("pricing.contactSales")}</a>
                    </Button>
                  ) : plan.earlyAccess ? (
                    <Button className="w-full" asChild>
                      <a href="mailto:support@testassets.io">{t("pricing.requestEarlyAccess")}</a>
                    </Button>
                  ) : (
                    <Button className="w-full" asChild>
                      <Link to="/auth">{t("pricing.getStarted")}</Link>
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Feature Comparison Table */}
          <section className="mb-20" aria-labelledby="pricing-comparison-heading">
            <h2 id="pricing-comparison-heading" className="text-3xl font-bold text-foreground text-center mb-8">
              {t("pricing.comparison.title")}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-4 text-sm font-medium text-muted-foreground w-1/5">
                      {t("pricing.comparison.feature")}
                    </th>
                    {planNames.map((name, i) => (
                      <th key={i} className={`text-center py-4 px-4 text-sm font-semibold ${i === 2 ? 'text-primary' : 'text-foreground'}`}>
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((row, ri) => (
                    <tr key={ri} className="border-b border-border/50">
                      <td className="py-3 px-4 text-sm text-foreground font-medium">{row.label}</td>
                      {row.values.map((val, ci) => (
                        <td key={ci} className="text-center py-3 px-4">
                          {val === true ? (
                            <Check className="h-5 w-5 text-primary mx-auto" />
                          ) : val === false ? (
                            <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                          ) : (
                            <span className="text-sm text-muted-foreground">{val}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-3xl mx-auto mb-16" aria-labelledby="pricing-faq-heading">
            <div className="flex items-center justify-center gap-3 mb-8">
              <HelpCircle className="h-6 w-6 text-primary" />
              <h2 id="pricing-faq-heading" className="text-3xl font-bold text-foreground">
                {t("pricing.faq.title")}
              </h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger>{item.q}</AccordionTrigger>
                  <AccordionContent>{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Custom Solution */}
          <section className="bg-card border border-border rounded-lg p-8 text-center" aria-labelledby="pricing-custom-heading">
            <h2 id="pricing-custom-heading" className="text-2xl font-bold text-foreground mb-4">
              {t("pricing.customSolution.title")}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              {t("pricing.customSolution.description")}
            </p>
            <Button size="lg" asChild>
              <a href="mailto:support@testassets.io">{t("pricing.contactSales")}</a>
            </Button>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
